import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Zap, TrendingUp } from "lucide-react";

/**
 * TopStep / ProjectX futures broker card.
 *
 * Flow:
 *   1. User enters TopstepX username + API key → POST /brokers/topstep/connect
 *      Backend validates via /api/Auth/loginKey, then returns the list of active
 *      TopStep accounts.
 *   2. User picks which account to trade on → POST /brokers/topstep/select-account
 *   3. Card shows live balance / canTrade status.
 *
 * The actual trading params (contracts, $SL, $TP, allowed symbols) live in the
 * Settings page under the "TopStep Futures" section — so this card focuses on
 * the connection lifecycle only.
 */
export default function TopStepCard({ status, onChanged }) {
  const [form, setForm] = useState({ username: "", api_key: "" });
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  const connected = !!status?.topstep_connected;

  // Auto-fill the username field on every mount / status refresh so the user
  // never has to retype it when rotating their ProjectX API key or reconnecting.
  useEffect(() => {
    if (status?.topstep_username && !form.username) {
      setForm((f) => ({ ...f, username: status.topstep_username }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.topstep_username]);

  useEffect(() => {
    if (connected && status?.topstep_account_id) {
      setSelectedAccountId(String(status.topstep_account_id));
    }
  }, [connected, status?.topstep_account_id]);

  // Load the account list once connected (so user can switch accounts anytime).
  const loadAccounts = async () => {
    try {
      const { data } = await api.get("/brokers/topstep/accounts");
      setAccounts(data.accounts || []);
    } catch (e) {
      /* silent — the connect flow will populate it too */
    }
  };
  useEffect(() => {
    if (connected) loadAccounts();
  }, [connected]);

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/brokers/topstep/connect", {
        username: form.username.trim(),
        api_key: form.api_key.trim(),
      });
      setAccounts(data.accounts || []);
      toast.success(`TopStep connected — ${data.accounts?.length || 0} account(s) available`);
      setForm({ ...form, api_key: "" });
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "TopStep connection failed");
    } finally {
      setBusy(false);
    }
  };

  const selectAccount = async (id, name) => {
    try {
      await api.post("/brokers/topstep/select-account", {
        account_id: Number(id),
        account_name: name || null,
      });
      setSelectedAccountId(String(id));
      toast.success(`Trading account set to ${name || id}`);
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to select account");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect TopStep? Your API key will be removed.")) return;
    try {
      await api.post("/brokers/topstep/disconnect");
      setAccounts([]);
      setSelectedAccountId("");
      toast.success("TopStep disconnected");
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };

  return (
    <div className="border border-white/10 bg-[#121214] rounded-sm" data-testid="topstep-card">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <div className="font-medium">TopStep <span className="text-zinc-500 font-normal">/ Futures</span></div>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">via ProjectX</span>
        </div>
        {connected ? (
          <div className="flex items-center gap-2 text-xs font-mono" data-testid="topstep-status-connected">
            {status?.topstep_can_trade
              ? <><span className="dot-online" /><span className="text-teal-400">Trading enabled</span></>
              : status?.topstep_error
              ? <><span className="dot-offline" style={{background:'#fb7185'}} /><span className="text-rose-400">Error</span></>
              : <><span className="dot-warn" /><span className="text-amber-400">Connected</span></>}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono">
            <span className="dot-offline" /> Not configured
          </div>
        )}
      </div>

      {connected ? (
        <div className="p-4 space-y-3 font-mono text-sm" data-testid="topstep-info">
          <div className="flex justify-between">
            <span className="text-zinc-500">Username</span>
            <span data-testid="topstep-username">{status.topstep_username}</span>
          </div>
          {status.topstep_account_name && (
            <div className="flex justify-between">
              <span className="text-zinc-500">Account</span>
              <span data-testid="topstep-account-name">{status.topstep_account_name}</span>
            </div>
          )}
          {status.topstep_balance != null && (
            <div className="flex justify-between">
              <span className="text-zinc-500">Balance</span>
              <span className="tabular-nums" data-testid="topstep-balance">
                ${Number(status.topstep_balance).toFixed(2)}
              </span>
            </div>
          )}

          {/* Account picker */}
          {accounts.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Trading Account
              </label>
              <select
                data-testid="topstep-account-select"
                value={selectedAccountId}
                onChange={(e) => {
                  const acct = accounts.find((a) => String(a.id) === e.target.value);
                  selectAccount(e.target.value, acct?.name);
                }}
                className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm focus:border-zinc-500 focus:outline-none"
              >
                <option value="">— select an account —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name || `Account ${a.id}`} · ${Number(a.balance || 0).toFixed(0)}
                    {a.canTrade ? "" : " · locked"}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-600 mt-1">
                One TopStep API key can access multiple accounts (Combine, Express, Funded).
                Only the selected account fires trades.
              </div>
            </div>
          )}

          {status.topstep_error && (
            <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 p-2 rounded-sm break-all leading-relaxed" data-testid="topstep-error-banner">
              <div className="text-rose-400 font-medium mb-1 uppercase tracking-widest text-[10px]">TopStep Error</div>
              {status.topstep_error}
            </div>
          )}

          <div className="text-xs text-zinc-500 pt-2 flex items-start gap-1.5">
            <TrendingUp className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-400" />
            <span>Set contract size, $SL, $TP and choose which futures (GC, MGC, NQ, MNQ) to trade in <span className="text-zinc-300">Settings → TopStep Futures</span>.</span>
          </div>

          <button
            onClick={disconnect}
            data-testid="topstep-disconnect-button"
            className="mt-2 text-xs uppercase tracking-widest text-rose-400 hover:text-rose-300"
          >
            Remove TopStep credentials
          </button>
        </div>
      ) : (
        <form onSubmit={connect} className="p-4 space-y-3">
          <div className="text-xs text-zinc-500 leading-relaxed pb-1">
            Enter your <span className="text-zinc-300">TopstepX username</span> and the <span className="text-zinc-300">API key</span> you generated at
            {" "}<span className="text-amber-400">TopstepX → Settings → API</span>.
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">TopstepX Username</label>
            <input
              type="text"
              data-testid="topstep-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              autoComplete="username"
              placeholder="e.g. woodvixa92"
              className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">API Key</label>
            <input
              type="password"
              data-testid="topstep-apikey-input"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              autoComplete="new-password"
              placeholder="paste the key from Settings → API tab"
              className="w-full mt-1 bg-black border border-white/10 rounded-sm px-2 py-1.5 text-sm font-mono focus:border-zinc-500 focus:outline-none"
              required
            />
            <div className="text-[10px] text-zinc-600 mt-1">
              Click the 👁 eye icon in TopstepX to reveal the key (it&apos;s a long random string, not a timestamp).
            </div>
          </div>
          <button
            data-testid="topstep-connect-button"
            disabled={busy}
            className="w-full bg-amber-500/90 hover:bg-amber-400 text-black py-2 rounded-sm text-sm font-medium disabled:opacity-60"
          >
            {busy ? "Connecting…" : "Connect TopStep"}
          </button>
          <div className="text-[10px] text-zinc-600">
            Your credentials are stored server-side and used only to authenticate with the ProjectX Gateway API.
          </div>
        </form>
      )}
    </div>
  );
}
